import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, model, output, viewChildren } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SvgIconComponent, SvgIcons, injectRegisterIcons } from '@ngneat/svg-icon';
import { filter } from 'rxjs';

import { IfPermissionDirective } from '@shared/directives/if-permission';

import { appChevronDown24Icon } from '@icons/px24/chevron-down-24';
import { appChevronUp24Icon } from '@icons/px24/chevron-up-24';

import { SideMenuNestedItemForcedHighlightPipe } from './side-menu-nested-item-forced-highlight.pipe';
import { SideMenuNestedItemLinkIsActiveDirective } from './side-menu-nested-item-link-is-active.directive';
import { SideMenuNestedItemViewModel } from './side-menu-nested-item.view-model';

@Component({
  selector: 'app-side-menu-nested-item',
  templateUrl: './side-menu-nested-item.component.html',
  styleUrls: ['./side-menu-nested-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('openClose', [
      transition(':enter', [
        style({ opacity: 0, overflow: 'hidden' }),
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1, overflow: 'hidden' }),
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
  imports: [
    NgClass,
    RouterLink,
    RouterLinkActive,
    SvgIconComponent,
    SideMenuNestedItemLinkIsActiveDirective,
    SideMenuNestedItemForcedHighlightPipe,
    IfPermissionDirective,
  ],
})
export class SideMenuNestedItemComponent {
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);

  public isSideMenuOpen = input.required<boolean>();
  public isMenuOpen = model.required<boolean>();
  public routes = input.required<SideMenuNestedItemViewModel[]>();
  public iconKey = input.required<SvgIcons>();
  public label = input.required<string>();

  public navigationClick = output<MouseEvent>();

  public sideMenuNestedItemLinkIsActiveDirectives = viewChildren(SideMenuNestedItemLinkIsActiveDirective);

  readonly #navigationEnd = toSignal(
    this.#router.events.pipe(
      takeUntilDestroyed(this.#destroyRef),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    )
  );

  public readonly highlightedGroup = computed(() => {
    // run highlight for the group after every navigation end event
    this.#navigationEnd();

    return this.sideMenuNestedItemLinkIsActiveDirectives().some((sideMenuNestedItemLinkIsActiveDirective) =>
      sideMenuNestedItemLinkIsActiveDirective.isActive()
    );
  });

  constructor() {
    injectRegisterIcons([appChevronUp24Icon, appChevronDown24Icon]);
  }

  public onMenuClick(isMenuOpen: boolean): void {
    this.isMenuOpen.set(!isMenuOpen);
  }

  public onLabelClick(isShortcut: boolean | undefined, event: MouseEvent): void {
    if (isShortcut) this.isMenuOpen.set(false);

    this.navigationClick.emit(event);
  }
}
